/**
 * Collapsible glossary for every panel/metric on the dashboard. Native
 * <details>/<summary> — no JS state, keyboard-accessible, collapsed by
 * default. Lives at the bottom of the control rail.
 */

const SECTIONS: Array<{
  title: string;
  entries: Array<[term: string, explanation: string]>;
}> = [
  {
    title: "Headline metrics",
    entries: [
      [
        "RTP",
        "return to player — long-run % of wagers paid back. ± value is the 95% confidence interval half-width: the true RTP is inside that band 95 times out of 100.",
      ],
      ["Hit frequency", "% of spins that pay anything at all."],
      [
        "Volatility (σ)",
        "standard deviation of per-spin payouts. Higher = rarer but bigger wins; lower = frequent small wins. Class (low/medium/high) is the banded label.",
      ],
      [
        "Max win",
        "largest single-spin win observed, in stake multiples. Bound = the validator's proven ceiling.",
      ],
    ],
  },
  {
    title: "Charts",
    entries: [
      [
        "Win distribution",
        "spin counts per win-size bucket, log scale — most spins land in the small buckets.",
      ],
      [
        "RTP by symbol / by rule",
        "how much each symbol or pay rule contributes to total RTP.",
      ],
      ["Feature triggers", "how often each bonus feature fires, per spin."],
      ["Bonus split", "share of RTP from the base game vs each bonus feature."],
    ],
  },
  {
    title: "RTP convergence",
    entries: [
      [
        "Cumulative",
        "running RTP from spin 1 — converges toward the long-run value.",
      ],
      [
        "Rolling",
        "RTP over the last N spins only — shows local variance/streaks.",
      ],
    ],
  },
  {
    title: "Pay rules",
    entries: [
      ["Hits", "spins where the rule paid."],
      ["Hit rate", "hits ÷ total spins."],
      ["Avg pay", "average payout per hit, in stake multiples."],
      ["RTP", "the rule's contribution to total RTP."],
    ],
  },
  {
    title: "Run history",
    entries: [
      [
        "Δ",
        "change vs the previous run of the same config (▲ up · ▼ down · “·” ≈ no change · blank = first run of that config).",
      ],
      ["Cfg", "config-hash prefix — shown when runs span multiple configs."],
      ["ms", "wall-clock run time."],
    ],
  },
  {
    title: "Footer",
    entries: [
      [
        "config_hash",
        "canonical-JSON SHA-256 of the game config — pin it with the seed to replay any spin bit-exactly.",
      ],
      ["schema", "MetricsResult schema version."],
    ],
  },
];

export function LegendPanel() {
  return (
    <details className="group border border-paper-border dark:border-term-border">
      <summary className="cursor-pointer select-none p-3 text-[11px] font-semibold uppercase tracking-widest text-paper-bright marker:content-none dark:text-term-bright">
        <span className="mr-1.5 inline-block text-paper-label transition-transform group-open:rotate-90 dark:text-term-label">
          ▸
        </span>
        Legend
      </summary>
      <div className="space-y-3 border-t border-paper-border p-3 dark:border-term-border">
        {SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="mb-1.5 border-b border-paper-border pb-1 text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:border-term-border dark:text-emerald-500">
              {section.title}
            </div>
            <dl className="space-y-1">
              {section.entries.map(([term, explanation]) => (
                <div key={term} className="text-[11px] leading-snug">
                  <dt className="inline font-semibold text-paper-text dark:text-term-text">
                    {term}
                  </dt>{" "}
                  <dd className="inline text-paper-label dark:text-term-label">
                    — {explanation}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </details>
  );
}
